import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'

import en from 'src/constants/en'
import { InsufficientCreditsError } from 'src/interfaces/credits'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { AiChatApi } from 'src/services/aiChatApi'

import {
  SendAiChatMessageVariables,
  UseSendAiChatMessageMutationParams,
} from './interfaces/useSendAiChatMessageMutation.interface'

export const useSendAiChatMessageMutation = ({
  onError,
  onInsufficientCredits,
  onSuccess,
}: UseSendAiChatMessageMutationParams = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ content, conversationId }: SendAiChatMessageVariables) =>
      AiChatApi.sendMessage(conversationId, { content }),
    onError: (error: AxiosError<InsufficientCreditsError>) => {
      // Check for insufficient credits error (403 with INSUFFICIENT_CREDITS code)
      if (
        error.response?.status === 403 &&
        error.response?.data?.code === 'INSUFFICIENT_CREDITS'
      ) {
        // Invalidate credits query to refresh the data
        queryClient.invalidateQueries({
          queryKey: [QueryKey.GET_CREDITS],
        })
        onInsufficientCredits?.(error.response.data)
        return
      }

      toast.error(en.AI_CHAT_SEND_MESSAGE_ERROR)
      onError?.()
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_AI_CHAT_CONVERSATION, variables.conversationId],
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_AI_CHAT_CONVERSATIONS],
      })
      onSuccess?.(data)
    },
  })
}
